import { useState } from 'react';
import {
  surgeryData,
  surgeonList,
  assistantSurgeonList,
  anesthesiologistList,
  nursingStaffList,
} from '../../../../utils/dataConstants';

export const INITIAL_FORM_STATE = {
  ip_no: "",
  uhid_no: "",
  patient_name: "",
  booking_datetime: "",
  allergies_medical_alerts: [],
  type_of_surgery: "",
  surgical_code: "",
  procedure_code: "",
  type_of_procedure: "",
  diagnosis_reason: "",
  expected_duration: "",
  surgery_datetime: "",
  preferred_operating_theatre: "",
  priority_status: "",
  primary_surgeon_name: "",
  primary_surgeon_id: "",
  assistant_surgeon_names: "",
  assistant_surgeon_ids: "",
  anesthesiologist_name: "",
  anesthesiologist_id: "",
  nursing_staff_names: "",
  nursing_staff_ids: "",
  preoperative_tests_required: false,
  special_equipment_needed: false,
  blood_products_required: false,
  fasting_instructions_given: false,
  preoperative_medications_provided: false,
  consent_forms_completed: false,
  is_major_ot: false,
  operation_status: "Scheduled",
  room_no: "",
  insurance_details: "",
  payment_method: "",
  card_type: "",
  patient_id: "",
  actual_start_time: "",
  actual_end_time: "",
};

const AUTO_FILL_MAP = {
  primary_surgeon_name: { list: surgeonList, matchField: "name", valueField: "id", idField: "primary_surgeon_id" },
  assistant_surgeon_names: { list: assistantSurgeonList, matchField: "name", valueField: "id", idField: "assistant_surgeon_ids" },
  anesthesiologist_name: { list: anesthesiologistList, matchField: "name", valueField: "id", idField: "anesthesiologist_id" },
  nursing_staff_names: { list: nursingStaffList, matchField: "name", valueField: "id", idField: "nursing_staff_ids" },
};

export const useOTBookingForm = (initialData = INITIAL_FORM_STATE) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});

  // Accepts both a plain object (full replace) or an updater function (partial update)
  const setInitialData = (dataOrUpdater) => setFormData(dataOrUpdater);

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));

    if (name === 'type_of_surgery') {
      const selected = surgeryData.find(s => s.diagnosisDescription === value);
      if (selected) {
        setFormData(prev => ({
          ...prev,
          type_of_surgery: value,
          surgical_code: selected.icd10Code,
          procedure_code: selected.procedureCode,
          type_of_procedure: selected.procedureDescription,
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else if (AUTO_FILL_MAP[name]) {
      const cfg = AUTO_FILL_MAP[name];
      const selected = cfg.list.find(item => item[cfg.matchField] === value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        [cfg.idField]: selected ? selected[cfg.valueField] : "",
      }));
    } else if (name === 'payment_method') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        insurance_details: value === 'Insurance' ? prev.insurance_details : ""
      }));
    } else if (name === 'insurance_details') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleAllergyChange = (newValue) => {
    setFormData(prev => ({ ...prev, allergies_medical_alerts: newValue }));
  };

  const handlePatientChange = (patient) => {
    setFormData(prev => ({
      ...prev,
      patient_name: patient?.name || "",
      uhid_no: patient?.uhid_no || "",
      patient_id: patient?.id || "",
    }));
    if (patient) setErrors(prev => ({ ...prev, uhid_no: '' }));
  };

  const validate = () => {
    const temp = {};
    if (!formData.uhid_no) temp.uhid_no = 'Please select a patient';
    if (!String(formData.ip_no).trim()) temp.ip_no = 'IP Number is required';
    if (!formData.surgery_datetime) temp.surgery_datetime = 'Surgery date & time is required';
    if (!formData.type_of_surgery) temp.type_of_surgery = 'Surgery type is required';
    if (!formData.primary_surgeon_name) temp.primary_surgeon_name = 'Surgeon name is required';
    if (!formData.priority_status) temp.priority_status = 'Priority status is required';
    if (!formData.preferred_operating_theatre) temp.preferred_operating_theatre = 'OT selection is required';
    if (!formData.room_no) temp.room_no = 'Room number is required';

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const buildPayload = () => ({
    ...formData,
    hospital_id: 1,
    allergies_medical_alerts: Array.isArray(formData.allergies_medical_alerts)
      ? formData.allergies_medical_alerts.join(', ')
      : formData.allergies_medical_alerts,
    surgery_duration: formData.expected_duration,
    assistant_surgeon_ids: formData.assistant_surgeon_ids ? parseInt(formData.assistant_surgeon_ids, 10) : 0,
    nursing_staff_ids: formData.nursing_staff_ids ? parseInt(formData.nursing_staff_ids) : 0,
    preoperative_tests_required: formData.preoperative_tests_required ? 1 : 0,
    special_equipment_needed: formData.special_equipment_needed ? 1 : 0,
    blood_products_required: formData.blood_products_required ? 1 : 0,
    fasting_instructions_given: formData.fasting_instructions_given ? 1 : 0,
    preoperative_medications_provided: formData.preoperative_medications_provided ? 1 : 0,
    consent_forms_completed: formData.consent_forms_completed ? 1 : 0,
    is_major_ot: formData.is_major_ot ? 1 : 0,
  });

  return {
    formData,
    errors,
    setInitialData,
    handleChange,
    handleAllergyChange,
    handlePatientChange,
    validate,
    buildPayload,
  };
};
